/**
 * Configurable non-labor cost settings for bids (§A): the mileage rate and the
 * debris-removal cost per quarter-truck. Stored as cents in the cost_settings
 * KV table. A bid total is labor + mileage (round-trip miles × rate) + debris
 * (quarters × per-quarter). Defaults match Leo's current figures.
 */
import { z } from 'zod';
/** $0.85/mi (IRS-derived; the supervisor bumps it when the rate changes). */
export const DEFAULT_MILEAGE_CENTS_PER_MILE = 85;
/** $30 per quarter-truck of debris — linear: ¼ $30 … full $120. */
export const DEFAULT_DEBRIS_CENTS_PER_QUARTER = 3000;
const CentsSchema = z.number().int().min(0).max(1_000_000);
export const CostSettingsSchema = z.object({
    mileage_per_mile_cents: CentsSchema,
    debris_per_quarter_cents: CentsSchema,
});
export const CostSettingsUpdateSchema = CostSettingsSchema.partial().refine((v) => Object.keys(v).length > 0, { message: 'Provide at least one setting to update.' });
/**
 * Pure bid math, shared by the editor and the API so they always agree.
 * Mileage = miles × rate; debris = quarters × per-quarter tier; total is the sum
 * plus the (already-combined) labor cost.
 */
export function computeBid(input, settings) {
    const labor_cents = Math.max(0, Math.round(input.labor_cents ?? 0));
    const mileage_cents = Math.max(0, Math.round((input.mileage_miles ?? 0) * settings.mileage_per_mile_cents));
    const debris_cents = Math.max(0, Math.round((input.debris_quarters ?? 0) * settings.debris_per_quarter_cents));
    return {
        labor_cents,
        mileage_cents,
        debris_cents,
        total_cents: labor_cents + mileage_cents + debris_cents,
    };
}
//# sourceMappingURL=cost-settings.js.map